import React from 'react';
import { ProjectLocationColumn } from './Locations.types';
import { Dropdown } from 'primereact/dropdown';
import { MultiSelect } from 'primereact/multiselect';

/**
 * Defines all static columns for the Project Locations table.
 * This function should ONLY be modified when intentionally adding/removing columns.
 *
 * WARNING: Tests will fail if columns are accidentally deleted from this array.
 * This is intentional to prevent accidental regressions.
 */
export const getStaticDefaultLocationColumns = (
    types: any,
    projectLocationListFilters: any,
    statusOptions: any,
    activeOptions: any,
    zeroDateFilters: any,
    baselines: any,
    projectManagersFilters: any,
    brands: any,
    continents: any,
    managementCompanies: any,
    areas: any,
    regions: any,
    countries: any,
    statusBodyTemplate: any,
    zeroDateBodyTemplate: any,
    baselineBodyTemplate: any,
    isOnHoldTemplate: any,
    isActiveTemplate: any,
    actionBodyTemplate: any,
    filterChange: any,
    keyUpFilter: any,
    onStatusChange: any,
    onMultiSelectChange: any,
    onDropDownChange: any
): Array<ProjectLocationColumn> => {
    return [
        {
            field: 'locationName',
            header: 'Name',
            isFeatureEnabled: true,
            default: true,
            isSortable: true,
            filterElement: (
                <input
                    type='text'
                    name='locationName'
                    className='p-inputtext'
                    placeholder='Filter by Name'
                    onBlur={filterChange}
                    onKeyUp={keyUpFilter}
                    defaultValue={projectLocationListFilters.locationName || ''}
                />
            ),
            style: { width: '300px' },
        },
        {
            field: 'locationAbbreviation',
            header: 'Abbreviation',
            isFeatureEnabled: true,
            default: true,
            isSortable: true,
            filterElement: (
                <input
                    type='text'
                    name='locationAbbreviations'
                    className='p-inputtext'
                    placeholder='Filter by Abbreviation'
                    onBlur={filterChange}
                    onKeyUp={keyUpFilter}
                    defaultValue={projectLocationListFilters.locationAbbreviations || ''}
                />
            ),
            style: { width: '100px' },
        },
        {
            field: 'status',
            header: 'Status',
            isFeatureEnabled: true,
            default: true,
            isSortable: true,
            body: statusBodyTemplate,
            filterElement: (
                <Dropdown
                    name='isPushedDown'
                    className='ui-column-filter w-100'
                    value={projectLocationListFilters.selectedStatus}
                    options={statusOptions}
                    optionLabel='label'
                    optionValue='value'
                    onChange={(e) => onStatusChange(e.value)}
                />
            ),
            style: { width: '110px' },
        },
        {
            field: 'zeroDate',
            header: 'Zero Date',
            isFeatureEnabled: true,
            default: true,
            isSortable: true,
            body: zeroDateBodyTemplate,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    dataKey='id'
                    placeholder='Select'
                    value={projectLocationListFilters.projectZeroDates}
                    options={zeroDateFilters}
                    onChange={(e) => onMultiSelectChange('projectZeroDates', 'zeroDates', e.value)}
                    maxSelectedLabels={1}
                />
            ),
            style: { width: '100px' },
        },
        {
            field: 'baselines',
            header: 'Baselines Assigned',
            isFeatureEnabled: true,
            default: true,
            isSortable: false,
            body: baselineBodyTemplate,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    dataKey='id'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.selectedBaselines}
                    options={baselines}
                    onChange={(e) => onMultiSelectChange('selectedBaselines', 'baselineIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'projectManager',
            header: 'Implementation Manager',
            isFeatureEnabled: true,
            default: true,
            isSortable: false,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    dataKey='id'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.selectedProjectManagers}
                    options={projectManagersFilters}
                    onChange={(e) => onMultiSelectChange('selectedProjectManagers', 'projectManagerIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'isOnHold',
            header: 'On Hold?',
            isFeatureEnabled: true,
            default: true,
            isSortable: true,
            body: isOnHoldTemplate,
            filterElement: (
                <Dropdown
                    optionLabel='label'
                    optionValue='value'
                    name='isOnHold'
                    className='ui-column-filter w-100'
                    value={projectLocationListFilters.isOnHold}
                    options={activeOptions}
                    onChange={(e) => onDropDownChange('isOnHold', 'isOnHold', e.value)}
                />
            ),
            style: { width: '84px' },
        },
        {
            field: 'isActive',
            header: 'Active?',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            body: isActiveTemplate,
            filterElement: (
                <Dropdown
                    optionLabel='label'
                    optionValue='value'
                    name='isActive'
                    className='ui-column-filter w-100'
                    value={projectLocationListFilters.isActive}
                    options={activeOptions}
                    onChange={(e) => onDropDownChange('isActive', 'isActive', e.value)}
                />
            ),
            style: { width: '80px' },
        },
        {
            field: 'brandName',
            header: 'Brand',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.brands}
                    options={brands}
                    onChange={(e) => onMultiSelectChange('brands', 'brandIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'continentName',
            header: 'Continent',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.continents}
                    options={continents}
                    onChange={(e) => onMultiSelectChange('continents', 'continentIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'managementCompanyName',
            header: 'Management Company',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.managementCompanies}
                    options={managementCompanies}
                    onChange={(e) => onMultiSelectChange('managementCompanies', 'managementCompanyIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'areaName',
            header: 'Area',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.areas}
                    options={areas}
                    onChange={(e) => onMultiSelectChange('areas', 'areaIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'regionName',
            header: 'Region',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.regions}
                    options={regions}
                    onChange={(e) => onMultiSelectChange('regions', 'regionIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'typeName',
            header: 'Type',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.types}
                    options={types}
                    onChange={(e) => onMultiSelectChange('types', 'typeIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'countryName',
            header: 'Country',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <MultiSelect
                    className='ui-column-filter'
                    optionLabel='name'
                    placeholder='Select'
                    value={projectLocationListFilters.countries}
                    options={countries}
                    onChange={(e) => onMultiSelectChange('countries', 'countryIds', e.value)}
                    maxSelectedLabels={1}
                />
            ),
        },
        {
            field: 'generation',
            header: 'Generation',
            isFeatureEnabled: true,
            default: false,
            isSortable: true,
            filterElement: (
                <input
                    type='text'
                    name='generation'
                    className='p-inputtext'
                    placeholder='Filter by Generation'
                    onBlur={filterChange}
                    onKeyUp={keyUpFilter}
                    defaultValue={projectLocationListFilters.generation || ''}
                />
            ),
        },
        {
            field: 'actions',
            optionLabel: 'Actions',
            header: '',
            isFeatureEnabled: true,
            default: true,
            isSortable: false,
            body: actionBodyTemplate,
            style: {
                width: '260px',
                textAlign: 'right',
            },
            reorderable: false,
        },
    ];
};

/**
 * Returns the expected column fields. This serves as a reference point for tests.
 * If this list changes, tests will catch it and fail.
 */
export const EXPECTED_COLUMN_FIELDS = [
    'locationName',
    'locationAbbreviation',
    'status',
    'zeroDate',
    'baselines',
    'projectManager',
    'isOnHold',
    'isActive',
    'brandName',
    'continentName',
    'managementCompanyName',
    'areaName',
    'regionName',
    'typeName',
    'countryName',
    'generation',
    'actions',
] as const;

/**
 * Returns the expected number of columns.
 * This serves as a quick regression check.
 */
export const EXPECTED_COLUMN_COUNT = EXPECTED_COLUMN_FIELDS.length;

/**
 * Column metadata for testing purposes.
 */
export const COLUMN_METADATA = {
    totalCount: EXPECTED_COLUMN_COUNT,
    defaultColumns: EXPECTED_COLUMN_FIELDS.filter((field) => {
        const defaultFields = [
            'locationName',
            'locationAbbreviation',
            'status',
            'zeroDate',
            'baselines',
            'projectManager',
            'isOnHold',
            'isActive',
            'actions',
        ];
        return defaultFields.includes(field);
    }),
    optionalColumns: EXPECTED_COLUMN_FIELDS.filter((field) => {
        const defaultFields = [
            'locationName',
            'locationAbbreviation',
            'status',
            'zeroDate',
            'baselines',
            'projectManager',
            'isOnHold',
            'isActive',
            'actions',
        ];
        return !defaultFields.includes(field);
    }),
};
